import { Input, InputNumber, Select } from '@aigc/antd';
import { AiModal } from '@aigc/components';
import { useSubmitFn } from '@aigc/hooks';
import { useOverlay, useOverlayRef } from '@aigc/overlay-hook';
import { useLens } from '@hookform/lenses';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Form } from 'antd';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { v7 as uuid } from 'uuid';
import { number, object, string } from 'yup';
import type { UploadFileOutputDto } from '../features/file/model';
import { createScriptMutation } from '../features/script/api';
import { ScriptType } from '../features/script/model';
import type { ScriptDto } from '../features/script/script.dto';
import type { ScriptTagDto } from '../features/tag/model';
import { PromptControl } from '../features/video-model/components/prompt-control';
import { GenerationType } from '../features/video-model/model-spec';
import { FormControl } from './form-control';
import ImageSelector from './image-selector';
import { ProductImageSelectionModal } from './product-image-selection-modal';
import { TagSelectorModal } from './tag-selector-modal';

interface ParsedScene {
  startTime?: string;
  endTime?: string;
  shotType?: string;
  visualDetail?: string;
  subtitle?: string;
  audio?: string;
}

interface ParsedData {
  aspectRatio: string;
  duration: string;
  scenes: ParsedScene[];
  extraNotes: string;
}

export const AddScriptModal = () => {
  const overlayRef = useOverlayRef<boolean>();
  const [openOverlay] = useOverlay<ScriptTagDto[]>();
  const { message, notification } = App.useApp();
  const [selectedTags, setSelectedTags] = useState<ScriptTagDto[]>([]);
  const [pasteText, setPasteText] = useState('');
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [firstFrameModalOpen, setFirstFrameModalOpen] = useState(false);
  const [selectedFirstFrame, setSelectedFirstFrame] =
    useState<UploadFileOutputDto | null>(null);

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      prompt: '',
      product_name: '',
      duration: 5,
      aspect_ratio: '9:16',
    },
    resolver: yupResolver(
      object({
        duration: number().nullable().required(),
        aspect_ratio: string().nullable(),
        prompt: string().nullable().required(),
        product_name: string().nullable().required('请输入产品名称'),
      }),
    ),
  });

  const lens = useLens({ control });

  const handleSelectFirstFrame = (image: UploadFileOutputDto) => {
    setSelectedFirstFrame(image);
  };

  const aspectRatioOptions = [
    { label: '9:16 竖屏', value: '9:16' },
    { label: '16:9 横屏', value: '16:9' },
    { label: '1:1 方形', value: '1:1' },
    { label: '4:5', value: '4:5' },
  ];

  const extractAspectRatio = (text: string) => {
    const ratioMatch = text.match(/(\d+:\d+)/);
    return ratioMatch ? ratioMatch[1] : null;
  };

  const parsePasteText = () => {
    if (!pasteText.trim()) {
      message.info('请粘贴表格数据');
      return;
    }

    const lines = pasteText
      .trim()
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line);
    if (lines.length < 2) {
      notification.error({
        title: '错误',
        description: '数据格式不正确，请检查',
      });
      return;
    }

    const parsedScenes: ParsedScene[] = [];
    let detectedAspectRatio: string | null | undefined = null;
    let maxEndTime = 0;
    let extraNotesText = '';

    detectedAspectRatio = extractAspectRatio(pasteText);

    let dataLines = lines;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i] as string;
      if (extractAspectRatio(line) || line.includes('执行标准')) {
        detectedAspectRatio = extractAspectRatio(line);
        extraNotesText = line as string;
        dataLines = lines.slice(0, i);
        break;
      }
    }

    if (dataLines.length === 0) {
      dataLines = lines;
    }

    const headerLine = dataLines[0] as string;
    const cells = headerLine.split(/\t+/).map((cell) => cell.trim());
    const hasTimeColumn = cells.some(
      (cell) => cell.includes('时间轴') || cell.includes('时间'),
    );

    if (!hasTimeColumn) {
      notification.error({
        title: '错误',
        description: '未找到时间轴列，请检查数据格式',
      });
      return;
    }

    for (let i = 1; i < dataLines.length; i++) {
      const line = dataLines[i] as string;
      const rowCells = line.split(/\t+/).map((cell) => cell.trim());

      if (rowCells[0] && rowCells[0].includes('-')) {
        const timeMatch = rowCells[0].match(/([\d.]+)\s*-\s*([\d.]+)/);
        if (timeMatch) {
          const startTime = timeMatch[1];
          const endTime = timeMatch[2] as string;

          if (parseFloat(endTime) > maxEndTime) {
            maxEndTime = parseFloat(endTime);
          }

          parsedScenes.push({
            startTime,
            endTime,
            shotType: rowCells[1] || '',
            visualDetail: rowCells[2] || '',
            subtitle: rowCells[3] || '',
            audio: rowCells[4] || '',
          });
        }
      }
    }

    if (parsedScenes.length === 0) {
      notification.error({
        title: '错误',
        description: '未能解析到任何时间片段，请检查数据格式',
      });
      return;
    }

    setValue(
      'prompt',
      parsedScenes
        .map((item) => {
          const duration = +(item.endTime ?? '0') - +(item.startTime ?? '0');

          const text: string[] = [`时长：${duration}`];
          if (item.shotType) {
            text.push(`镜头模块: ${item.shotType}`);
          }
          if (item.visualDetail) {
            text.push(`视觉细节: ${item.visualDetail}`);
          }
          if (item.subtitle) {
            text.push(`旁白: ${item.subtitle}`);
          }
          if (item.audio) {
            text.push(`音效与节奏: ${item.audio}`);
          }

          return text.join(' | ');
        })
        .concat([extraNotesText])
        .join('\n'),
    );

    setParsedData({
      aspectRatio: detectedAspectRatio || '9:16',
      duration: maxEndTime.toString(),
      scenes: parsedScenes,
      extraNotes: extraNotesText,
    });
  };

  const clearPaste = () => {
    setPasteText('');
    setParsedData(null);
  };

  const createScript = useMutation(createScriptMutation);

  const handleSave = useSubmitFn(
    handleSubmit(
      async (values) => {
        const data: ScriptDto = {
          id: uuid(),
          generation_type: GenerationType.Frame,
          aspect_ratio: values.aspect_ratio ?? '',
          duration: values.duration,
          file_list: selectedFirstFrame
            ? [
                {
                  name: selectedFirstFrame.filename,
                  type: selectedFirstFrame.mime,
                  url: selectedFirstFrame.file_url,
                },
              ]
            : [],
          resolution: '720p',
          prompt: values.prompt,
          product_name: values.product_name,
          product_description: '',
          segments: [],
          source: void 0,
          generate_audio: true,
        };

        lfq('track', 'script.save');

        await createScript.mutateAsync({
          tags: selectedTags,
          type: ScriptType.Video,
          data,
        });

        message.success('添加成功');
        overlayRef.close(true);
      },
      (error) => {
        console.error(error);
      },
    ),
  );

  const handleClose = () => {
    overlayRef.close();
  };

  return (
    <AiModal
      title="添加脚本"
      okText="保存"
      width={800}
      centered
      loading={createScript.isPending}
      onCancel={handleClose}
      onOk={() => {
        handleSave();
      }}
    >
      {parsedData && (
        <Button
          block
          size="large"
          variant="dashed"
          color="default"
          onClick={clearPaste}
        >
          清空
        </Button>
      )}
      <Form.Item layout="vertical" label="快速导入">
        <Input.TextArea
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          placeholder={`请将Excel表格复制粘贴到这里...
格式示例：
时间轴 (s)	镜头模块	画面视觉 & 运镜	旁白（VO）	音效与节奏
0.0 - 1.5	瞬间钩子 A	【POV/极近特写】...	Magic happens ✨	[SFX] 丝滑的拉链声"
                `}
          rows={8}
        ></Input.TextArea>
      </Form.Item>
      <Form.Item>
        <Button
          block
          size="large"
          variant="filled"
          color="default"
          onClick={() => {
            parsePasteText();
          }}
        >
          解析数据
        </Button>
      </Form.Item>
      {parsedData && (
        <div className="mt-4 space-y-3">
          <div className="rounded-lg border border-success-500/30 bg-success-500/10 p-3">
            <p className="text-sm text-success-400">
              ✅ 解析成功！共 {parsedData.scenes.length} 个时间片段
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormControl
          control={control}
          name="aspect_ratio"
          label="宽高比"
          layout="vertical"
          render={({ field }) => (
            <Select
              {...field}
              allowClear={false}
              options={aspectRatioOptions.map((item) => ({
                label: item.label,
                value: item.value,
              }))}
            ></Select>
          )}
        ></FormControl>
        <FormControl
          control={control}
          name="duration"
          label="时长"
          layout="vertical"
          render={({ field }) => (
            <InputNumber
              classNames={{ root: 'w-full' }}
              {...field}
              suffix="秒"
              min={0}
              precision={0}
            ></InputNumber>
          )}
        ></FormControl>
      </div>
      <PromptControl lens={lens.focus('prompt').cast()}></PromptControl>
      <div className="mb-2 flex items-center justify-between">
        <label className="block text-sm font-medium text-primary-600">
          标签
        </label>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex flex-1 flex-wrap gap-2">
          {selectedTags.length > 0 ? (
            selectedTags.map((tag) => (
              <span
                key={tag.id}
                className="rounded-full bg-primary-900/10 px-2 py-1 text-xs text-primary-500"
              >
                {tag.name}
              </span>
            ))
          ) : (
            <span className="text-sm text-primary-500">未选择标签</span>
          )}
        </div>
        <Button
          onClick={async () => {
            const overlayRef = openOverlay(
              <TagSelectorModal
                initialTagIds={selectedTags.map((item) => item.id)}
              />,
            );

            const result = await overlayRef.afterClosed;

            if (result == null) {
              return;
            }

            setSelectedTags(result);
          }}
          color="default"
          variant="filled"
        >
          选择标签
        </Button>
      </div>
      <ImageSelector
        selectedImage={selectedFirstFrame}
        onOpenModal={() => setFirstFrameModalOpen(true)}
        required={true}
        text="产品图"
      />
      <ProductImageSelectionModal
        isOpen={firstFrameModalOpen}
        onClose={() => setFirstFrameModalOpen(false)}
        onConfirm={handleSelectFirstFrame}
        currentImage={selectedFirstFrame}
      />
      <FormControl
        rootClassName="mt-4"
        control={control}
        name="product_name"
        required
        label="产品名称"
        layout="vertical"
        render={({ field }) => (
          <Input {...field} placeholder="输入产品名称"></Input>
        )}
      ></FormControl>
    </AiModal>
  );
};
